import { useState } from 'react';
import { BRAND } from '../config/landingContent';
import { resolveSaasLogoFromBranding, SAAS_DEFAULT_LOGO_URL } from '../lib/saasLogo';
import type { ResolvedCompanyBranding } from '../../utils/companyBranding';

const SIZE = {
  xs: { plate: 'h-7 w-7 rounded-md', px: 28 },
  sm: { plate: 'h-8 w-8 rounded-lg', px: 32 },
  md: { plate: 'h-10 w-10 rounded-xl', px: 40 },
  lg: { plate: 'h-24 w-24 rounded-3xl', px: 96 },
} as const;

/**
 * ServiceApp / tenant mark on a real light plate. The default platform PNG
 * is the S on white; the plate stays white in night mode so the mark never
 * sits on navy. `html.dark .bg-white` remaps do not apply here.
 */
export default function SaasBrandMark({
  src,
  branding,
  alt = '',
  size = 'sm',
  className = '',
}: {
  src?: string;
  branding?: Pick<ResolvedCompanyBranding, 'logoUrl' | 'companyName'>;
  alt?: string;
  size?: keyof typeof SIZE;
  className?: string;
}) {
  const preferred = branding ? resolveSaasLogoFromBranding(branding) : src?.trim() || BRAND.logo;
  const [imageSrc, setImageSrc] = useState(preferred);
  const box = SIZE[size];

  return (
    <span
      className={`saas-logo-plate inline-flex shrink-0 items-center justify-center overflow-hidden bg-white shadow-sm ring-1 ring-black/5 ${box.plate} ${className}`}
      aria-hidden={alt ? undefined : true}
    >
      <img
        src={imageSrc}
        alt={alt}
        className="h-full w-full object-contain"
        width={box.px}
        height={box.px}
        decoding="async"
        onError={() => {
          if (imageSrc !== SAAS_DEFAULT_LOGO_URL) setImageSrc(SAAS_DEFAULT_LOGO_URL);
        }}
      />
    </span>
  );
}
